import { useQuery } from '@tanstack/react-query';

import type { StorefrontSeller } from '@/features/storefront/api/storefront';
import { useShopContent, type ContentReader } from '@/features/storefront/content/useContent';
import { useStorefrontData, useStorefrontScope } from '@/features/storefront/data/context';
import { usePartsEnvironment } from '@/features/storefront/parts/context';
import type { ProductPartProps } from '@/features/storefront/parts/ProductParts';
import { partOwnsSpacing } from '@/features/storefront/parts/style';
import { STOREFRONT_HEADING_STYLE } from '@/features/storefront/theme';
import { useT } from '@/lib/i18n/context';

/**
 * The seller-information panel: who sells this, where they are, their GSTIN or
 * FSSAI licence and how to reach customer care - what Indian e-commerce rules
 * expect a product page to name.
 *
 * Every value is the shop's own business profile, read from the storefront
 * context's `seller` block, which the server leaves null until the merchant
 * switches "Show seller details on product pages" on. Null draws nothing; a
 * blank field is left out rather than shown empty.
 *
 * In the customiser the dashboard is not the shop's host, so nothing is asked
 * of the server and the panel shows the merchant where its details come from.
 */
export function ProductSellerInfoPart({ part }: ProductPartProps) {
  const text = useShopContent();
  const t = useT();
  const source = useStorefrontData();
  const scope = useStorefrontScope();
  const editor = usePartsEnvironment()?.includeHidden ?? false;

  const context = useQuery({
    queryKey: [...scope, 'context'],
    queryFn: () => source.context(),
    enabled: !editor,
  });

  const heading = part.label.trim() || text('product_page.seller_heading');
  const spacing = partOwnsSpacing(part) ? 'mt-6' : undefined;

  if (editor) {
    return (
      <div
        className={`${spacing ?? ''} border border-dashed p-4 text-sm`}
        style={{ borderRadius: 'var(--storefront-radius)' }}
        data-testid="seller-info-hint"
      >
        <p className="font-semibold" style={STOREFRONT_HEADING_STYLE}>
          {heading}
        </p>
        <p className="mt-1 opacity-70">{t('themeEditor.part_hints.product_seller_info')}</p>
      </div>
    );
  }

  const seller = context.data?.seller;
  if (!seller) return null;

  const rows = sellerRows(seller, text);
  return (
    <section
      className={`${spacing ?? ''} border p-4 text-sm`}
      style={{ borderRadius: 'var(--storefront-radius)', borderColor: 'var(--storefront-surface)' }}
      aria-label={heading}
    >
      <h2 className="text-sm font-semibold" style={STOREFRONT_HEADING_STYLE}>
        {heading}
      </h2>
      <dl className="mt-2 space-y-1.5">
        {rows.map(([label, value]) => (
          <div key={label} className="flex flex-wrap gap-x-2">
            <dt style={{ color: 'var(--storefront-muted)' }}>{label}</dt>
            <dd className="min-w-0 break-words font-medium">{value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/** The filled fields only, in the order a shopper reads them. */
function sellerRows(
  seller: StorefrontSeller,
  text: ContentReader,
): Array<[label: string, value: string]> {
  const rows: Array<[string, string | null | undefined]> = [
    [text('product_page.seller_name'), seller.name],
    [text('product_page.seller_address'), seller.address],
    [text('product_page.seller_gstin'), seller.gstin],
    [text('product_page.seller_fssai'), seller.fssai_number],
    [text('product_page.seller_email'), seller.email],
    [text('product_page.seller_phone'), seller.phone],
  ];
  return rows.filter((row): row is [string, string] => Boolean(row[1]?.trim()));
}
